import React from 'react';
import { VictoryPie } from 'victory';

const initialState = [
  {
    x: '',
    y: 100,
    color: 'var(--skeleton-loader-from)',
  },
];

export const PieChart = ({
  radius = 42,
  strokeWidth = 4,
  width = 42,
  height = 42,
  padAngle = 2,
  animationDuration = 1000,
  size = 100,
  animate,
  data,
  colors,
  onPressSegment,
  hover = false,
  activeSegment,
  content,
  zIndex,
  classes,
}: {
  animate?: boolean;
  data: any[];
  colors: string[];
  activeSegment?: string;
  onPressSegment?: Function;
  radius: number;
  padAngle?: number;
  strokeWidth: number;
  animationDuration: number;
  size: number;
  width: number;
  height: number;
  content: React.ReactNode;
  zIndex?: number;
  hover: boolean;
  classes?: string;
}) => {
  const chartHeight = height || size;
  const chartWidth = width || size;

  return (
    <div
      className={`relative flex-1 items-center justify-center mx-auto ${classes || ''}`}>
      <div
        className="absolute flex flex-col flex-1 w-full"
        style={{
          left: '50%',
          transform: 'translate(-50%, -50%)',
          top: '50%',
          zIndex: zIndex || 0,
        }}>
        {content}
      </div>

      <VictoryPie
        data={data.length === 0 ? initialState : data}
        colorScale={
          colors.length === 0 ? ['var(--skeleton-loader-from)'] : colors
        }
        height={chartHeight}
        width={chartWidth}
        padAngle={padAngle}
        radius={(dataPoint: any) => {
          const code =
            dataPoint.datum.code ||
            dataPoint.datum.Code ||
            dataPoint.datum.xName;

          if (dataPoint.hover && !dataPoint.active) {
            return activeSegment === `${code}_${dataPoint.datum.type}`
              ? radius + strokeWidth
              : radius - strokeWidth / 1.2;
          }

          // Vaults have the same code as the non-vault counterparts
          // If a type is explicitly set,
          // the activeSegment should be '{code}_{type}'
          // This is to avoid graph entries with the same code
          if (dataPoint.datum.type) {
            return activeSegment === `${code}_${dataPoint.datum.type}`
              ? radius + strokeWidth
              : radius - strokeWidth / 1.5;
          }

          return activeSegment === code
            ? radius + strokeWidth
            : radius - strokeWidth / 1.5;
        }}
        style={{ labels: { display: 'none' } }}
        innerRadius={(dataPoint: any) => {
          const code =
            dataPoint.datum.code ||
            dataPoint.datum.Code ||
            dataPoint.datum.xName;

          if (dataPoint.hover && !dataPoint.active) {
            return activeSegment === `${code}_${dataPoint.datum.type}`
              ? radius - strokeWidth * 1.2
              : radius + strokeWidth;
          }

          // Vaults have the same code as the non-vault counterparts
          // If a type is explicitly set,
          // the activeSegment should be '{code}_{type}'
          // This is to avoid graph entries with the same code
          if (dataPoint.datum.type) {
            return activeSegment === `${code}_${dataPoint.datum.type}`
              ? radius - strokeWidth * 1.5
              : radius + strokeWidth;
          }

          return activeSegment === code
            ? radius - strokeWidth * 1.5
            : radius + strokeWidth;
        }}
        labels={() => null}
        animate={
          animate && {
            duration: animationDuration,
            easing: 'linear',
          }
        }
        events={[
          {
            target: 'data',
            eventHandlers: {
              onClick: event => {
                return [
                  {
                    target: 'data',
                    mutation: ({ datum }) => {
                      if (datum.type) {
                        onPressSegment?.(`${datum.x}_${datum.type}`);
                      } else {
                        onPressSegment?.(datum.x);
                      }
                      return { active: true };
                    },
                  },
                ];
              },
              onMouseOver: event => {
                return [
                  {
                    target: 'data',
                    mutation: props => {
                      return { hover };
                    },
                  },
                ];
              },
              onMouseOut: event => {
                return [
                  {
                    target: 'data',
                    mutation: props => {
                      return { hover: false };
                    },
                  },
                ];
              },
            },
          },
        ]}
      />
    </div>
  );
};
